import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PunchupDonutChart, DonutSlice } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { CapacityType } from 'features/common/hooks/useEventCapacity';
import { TicketTypeStat } from 'hooks/useGetScanHistory';
import { lightenHex } from 'features/common/utils/chartColors';
import { common } from 'theme';

import styles from './PunchupAttendanceByType.module.css';

// 150 + the 185px grid (PunchupAttendanceByType.module.css) match the Ticket Inventory donuts exactly, so
// the two donut sections render at the same width and scale together across screen sizes.
const DONUT_SIZE = 150;
/** "Not in yet" pairs with "Checked in" as the SAME hue, lighter — the in-vs-not-in language of the event-level
 * attendance donut above it. 0.5 = halfway to white. */
const NOT_IN_FADE = 0.5;
/** Neutral "Yet to arrive" remainder — the same grey the inventory donuts use for their empty/available slice. */
const REMAINDER_COLOR = common.colors.grey19;

interface PunchupAttendanceByTypeProps {
	/** Per-type scanned-vs-sold aggregate, from the parent section's single `useGetScanHistory` read. */
	ticketTypeStats: TicketTypeStat[] | undefined;
	/** Per-type capacity (name + sellable `amount`), from the parent section's single `useEventCapacity` read. */
	capacityTypes: Pick<CapacityType, 'name' | 'amount'>[];
}

/** Per-ticket-type attendance, one small donut per type, as a second row in the Show-Day section: **Checked in**
 * (the type's colour) · **Not in yet** (same hue, lighter = sold − scanned) · **Yet to arrive** (neutral grey =
 * capacity − sold; omitted when capacity is unknown/uncapped → a sold-based two-band ring). Each donut mirrors the
 * event-level Attendance donut above it — the type name is the card **title** (inside the card), plus the slice
 * **legend** and a **help tooltip**. Renders `null` when nothing has sold. Presentational: scan + capacity data
 * arrive as props from `PunchupVenueOperationsSection` (one observer per query for the whole section — it does NOT
 * subscribe its own scan-history/capacity, which previously duplicated those observers); colour still resolves via
 * the shared map. */
export const PunchupAttendanceByType: FC<PunchupAttendanceByTypeProps> = ({ ticketTypeStats, capacityTypes }) => {
	const { t } = useTranslation();
	const { resolveByName } = useTicketTypeColors();

	const checkedInLabel = t('analytics-attendance-checkedin', 'Checked in');
	const notInYetLabel = t('analytics-attendance-not-in-yet', 'Not in yet');
	const emptySeatsLabel = t('analytics-venue-empty-seats', 'Empty seats');

	const capacityByName = useMemo(() => {
		const map = new Map<string, number>();
		capacityTypes.forEach((type) => map.set(type.name, type.amount));
		return map;
	}, [capacityTypes]);

	const donuts = useMemo(
		() =>
			(ticketTypeStats ?? [])
				.filter((stat) => stat.soldTickets > 0)
				.map((stat) => {
					const scanned = Math.min(stat.scannedTickets, stat.soldTickets);
					const notInYet = Math.max(0, stat.soldTickets - scanned);
					const capacity = capacityByName.get(stat.ticketTypeName) ?? 0;
					const emptySeats = capacity > stat.soldTickets ? capacity - stat.soldTickets : 0;
					const ringTotal = emptySeats > 0 ? capacity : stat.soldTickets;
					const color = resolveByName(stat.ticketTypeName);
					const slices: DonutSlice[] = [
						{ label: checkedInLabel, value: scanned, color },
						{ label: notInYetLabel, value: notInYet, color: lightenHex(color, NOT_IN_FADE) },
						{ label: emptySeatsLabel, value: emptySeats, color: REMAINDER_COLOR },
					].filter((slice) => slice.value > 0);
					return { id: stat.ticketTypeId, name: stat.ticketTypeName, scanned, ringTotal, slices };
				}),
		[ticketTypeStats, capacityByName, resolveByName, checkedInLabel, notInYetLabel, emptySeatsLabel]
	);

	if (donuts.length === 0) return null;

	return (
		<div className={styles.grid}>
			{donuts.map((donut) => (
				<PunchupDonutChart
					key={donut.id}
					title={donut.name}
					size={DONUT_SIZE}
					centerLabel={`${donut.scanned}/${donut.ringTotal}`}
					centerTooltip={t('analytics-attendance-center', '{{scanned}} of {{total}} checked in', {
						scanned: donut.scanned,
						total: donut.ringTotal,
					})}
					data={donut.slices}
					showLegend
					helpButton={
						<PunchupHelpButton
							mainText="Attendance for this ticket type — how many of its holders have checked in at the door, against the type's ring."
							codeText={
								'checked in = tickets of this type scanned\nnot in yet = sold − checked in\nempty seats = unsold capacity for this type'
							}
							secondaryText="The per-type companion to the Attendance donut above; the centre is this type's door count over its ring."
						/>
					}
				/>
			))}
		</div>
	);
};
